import { useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { useStores } from '../../../../../stores/useStores';
import { formatTime } from '../../../../../utils/time';
import { useWidgetAdaptiveText } from '../../../hooks';

import styles from './StampCardTimer.module.scss';

interface StampCardTimerProps {
  extended?: boolean;
  dataTestId?: string;
}

const DEFAULT_TIMER = '00:00:00';

function StampCardTimerComponent(props: StampCardTimerProps) {
  const { extended, dataTestId } = props;
  const { stampCardStore } = useStores();
  const { t } = useTranslation();
  const { currentStampCard } = stampCardStore;
  const [countdown, setCountdown] = useState<string>(DEFAULT_TIMER);
  const hasRefetchedRef = useRef(false);
  const { textRef } = useWidgetAdaptiveText({ padding: 4 });

  useEffect(() => {
    const expiresAt = currentStampCard?.expiresAt;
    if (!expiresAt) {
      setCountdown(DEFAULT_TIMER);
      hasRefetchedRef.current = false;
      return;
    }

    const updateCountdown = () => {
      const secondsLeft = dayjs(expiresAt).diff(dayjs(), 'seconds');
      if (secondsLeft < 0) {
        setCountdown(DEFAULT_TIMER);
        if (!hasRefetchedRef.current) {
          hasRefetchedRef.current = true;
          void stampCardStore.fetch();
        }
        return;
      }
      hasRefetchedRef.current = false;
      setCountdown(formatTime(secondsLeft, 's', t));
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [currentStampCard?.expiresAt, stampCardStore, t]);

  return (
    <div className={extended ? styles.containerExtended : styles.container} data-testid={dataTestId}>
      <span ref={textRef} className={extended ? styles.textExtended : styles.text}>
        {countdown}
      </span>
    </div>
  );
}

export const StampCardTimer = observer(StampCardTimerComponent);
